import React, { useState } from 'react';
import {
  Accordion,
  AccordionItem,
  RadioButton,
  RadioButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'carbon-components-react';
import './Questions.scss';
import Slider from '../Slider/Slider';
import { segmentTracking } from '../../lib/tracker';
import { object, pageTitle } from '../../constants/default';

interface SubSection {
  questionName: string;
  featureDesc: string;
  questions: [
    {
      question: string;
      from: string;
      to: string;
      answers: [
        {
          weightage: number;
        },
      ];
    },
  ];
}

interface Data {
  section: string;
  question: string;
  answers: [{ weightage: number; answer: string; desc: string }];
  subsection: SubSection[];
}

interface Questions {
  questionName: string;
  key: number;
  quesCount: number;
  ans: number[];
}

export interface SectionArray {
  key: number;
  questions: Questions[];
}

interface QuestionsProps {
  data: Data;
  indexKey: number;
  locale: string;
  sectionArray: SectionArray[];
  setSectionArray: (...args: any[]) => void;
  selectedSection: number;
  onSectionChange: (step: number) => void;
}

const Questions: React.FC<QuestionsProps> = ({
  data,
  indexKey,
  locale,
  sectionArray,
  setSectionArray,
  selectedSection,
  onSectionChange,
}) => {
  const [selectedStep, setSelectedStep] = useState<number>(0);

  const options = [{ value: 0, label: '', desc: '' }];
  data.answers.map((i) =>
    options.push({
      label: i.answer,
      value: Number(i.weightage),
      desc: i.desc,
    }),
  );
  const replacePlaceholder = (text: string): string => {
    text = text.replaceAll(/&lt;/g, '<').replace(/&gt;/g, '>');
    return text.replace(/<cclc>/g, locale);
  };
  const selectedIndex = options.findIndex((e) => e.value === selectedStep);
  const selectedDesc =
    selectedIndex > 0 ? replacePlaceholder(options[selectedIndex].desc) : '';
  const findIndex: number = sectionArray.findIndex((e) => e.key === indexKey);
  const onStepChange = (e: number) => {
    setSelectedStep(e);
    if (findIndex > -1) sectionArray[findIndex]['key'] = indexKey;
    else sectionArray.push({ key: indexKey, questions: [] });
    setSectionArray(sectionArray);
    onSectionChange(indexKey);
    const element: HTMLElement = document.getElementById(
      `trigger-${indexKey}`,
    ) as HTMLElement;
    element.focus();
  };

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeader>{data?.section}</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell>
            <div id={`trigger-${indexKey}`}>
              <h4 className="mt-0">{data?.question}</h4>
              <Slider
                steps={options}
                selectedStep={selectedStep}
                onStepChange={onStepChange}
              />
            </div>
            <div no-toc id={`trigger-anser-${indexKey}`}>
              {selectedStep && indexKey === selectedSection ? (
                <p
                  className="pBorder"
                  dangerouslySetInnerHTML={{
                    __html: selectedDesc as string,
                  }}
                ></p>
              ) : (
                <></>
              )}
              {selectedStep &&
              indexKey === selectedSection &&
              data?.subsection &&
              !!data?.subsection.length ? (
                <Accordion className="padding-0 custom-accordion">
                  {data?.subsection.map((sub, j) => {
                    const findQuesIndex = sectionArray[findIndex][
                      'questions'
                    ]?.findIndex((e) => e.key === j);
                    if (findQuesIndex && findQuesIndex === -1) {
                      const ansSub = Array.from(
                        { length: sub.questions.length },
                        () => 0,
                      );
                      sectionArray[findIndex]['questions']?.push({
                        questionName: sub.questionName,
                        key: j,
                        quesCount: sub.questions.length,
                        ans: ansSub,
                      });
                    }

                    return (
                      sub &&
                      sub.questions && (
                        <AccordionItem
                          title={sub.questionName}
                          key={j}
                          open
                          className="accordian-pr-0"
                        >
                          {!!sub.questions.length &&
                            sub?.questions.map((ques, k) => {
                              const answer =
                                sectionArray[findIndex]['questions'][j]['ans'];
                              const setRadioValue = (s: any) => {
                                setSectionArray((prevData: any) =>
                                  prevData.map((outerItem: any) =>
                                    outerItem.key === indexKey
                                      ? {
                                          ...outerItem,
                                          questions: outerItem.questions.map(
                                            (question: any) =>
                                              question.key === j
                                                ? {
                                                    ...question,
                                                    ans: question.ans.map(
                                                      (
                                                        ans: number,
                                                        index: number,
                                                      ) =>
                                                        index === k ? s : ans,
                                                    ),
                                                  }
                                                : question,
                                          ),
                                        }
                                      : outerItem,
                                  ),
                                );
                                segmentTracking('CTA Clicked', {
                                  pageTitle: pageTitle,
                                  object: object,
                                  resultValue: 'CTA clicked',
                                  CTA: 'Answer Selection',
                                  location: 'Assessment',
                                  action: '',
                                  field: '',
                                });
                              };
                              return (
                                <div key={`${j}_${k}`} className="mar1">
                                  <p>{ques.question}</p>
                                  <>
                                    <div className="radio-group-container">
                                      <ul className="radio-group-container padding-0">
                                        <li className="radio-button"></li>
                                        {!!ques?.answers.length &&
                                          ques?.answers.map((ans, l) => (
                                            <li
                                              key={`${j}_${k}_${l}`}
                                              className="radio-button"
                                            >
                                              {ans.weightage}
                                            </li>
                                          ))}
                                        <li className="radio-button"></li>
                                      </ul>
                                    </div>
                                    <div className="radio-group-container">
                                      <RadioButtonGroup
                                        name={`cra-radio-group-${k}-${j}`}
                                        className="radio-group-container"
                                        onChange={(e) => setRadioValue(e)}
                                        valueSelected={answer[k]}
                                      >
                                        <span className="radio-button">
                                          {ques.from}
                                        </span>
                                        {!!ques?.answers.length &&
                                          ques?.answers.map((answ, o) => (
                                            <RadioButton
                                              key={`${j}-${o}-${k}`}
                                              value={Number(answ.weightage)}
                                              id={`radio-${o}-${k}-${j}`}
                                              labelText=""
                                              hideLabel={true}
                                              className="radio-button"
                                            />
                                          ))}
                                        <span className="radio-button">
                                          {ques.to}
                                        </span>
                                      </RadioButtonGroup>
                                    </div>
                                  </>
                                </div>
                              );
                            })}
                        </AccordionItem>
                      )
                    );
                  })}
                </Accordion>
              ) : (
                <></>
              )}
            </div>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

export default Questions;
